import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PricingFAQ = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const faqData = [
    {
      question: "How does billing work?",
      answer: `Billing is processed monthly in advance. You'll be charged on the same date each month based on your selected plan. We accept all major credit cards and offer annual billing with a 20% discount.\n\nOverage charges are calculated at the end of each billing cycle and added to your next invoice. You can monitor your usage in real-time through our dashboard.`
    },
    {
      question: "What happens if I exceed my plan limits?",
      answer: `If you exceed your monthly message limit, overage charges apply at the rates shown in each plan. There are no service interruptions - your messaging continues to work seamlessly.\n\nWe'll send you notifications at 80% and 95% of your limit so you can upgrade your plan if needed. You can also set up automatic plan upgrades to avoid overage charges.`
    },
    {
      question: "Can I change my plan anytime?",
      answer: `Yes, you can upgrade or downgrade your plan at any time. Upgrades take effect immediately, while downgrades take effect at the start of your next billing cycle.\n\nWhen upgrading mid-cycle, you'll be charged a prorated amount for the remaining days. When downgrading, you'll receive account credit for the unused portion.`
    },
    {
      question: "Do you offer refunds?",
      answer: `We offer a 30-day money-back guarantee for new customers. If you're not satisfied with our service within the first 30 days, we'll provide a full refund.\n\nFor existing customers, we don't offer refunds for partial months, but you can cancel anytime and your service will continue until the end of your current billing period.`
    },
    {
      question: "What\'s included in Enterprise support?",
      answer: `Enterprise plans include dedicated account management, priority support with guaranteed response times, custom SLA agreements, and direct access to our engineering team.\n\nYou'll also get custom onboarding, regular business reviews, and the ability to request custom features or integrations based on your specific needs.`
    },
    {
      question: "Are there any setup fees?",
      answer: `No, there are no setup fees for any of our plans. You can start using our messaging infrastructure immediately after signing up.\n\nFor Enterprise customers requiring custom deployment or extensive onboarding, we may discuss implementation services separately, but the core platform access has no setup costs.`
    },
    {
      question: "How do you handle data security and compliance?",
      answer: `We maintain SOC 2 Type II compliance and are GDPR compliant. All data is encrypted in transit and at rest using industry-standard encryption.\n\nEnterprise plans include additional security features like custom data retention policies, audit logs, and the option for dedicated infrastructure or on-premise deployment.`
    },
    {
      question: "Can I get a custom quote for high-volume usage?",
      answer: `Absolutely! If you expect to send more than 10 million messages per month or need custom features, our Enterprise team can create a tailored solution.\n\nContact our sales team to discuss volume discounts, custom SLAs, dedicated infrastructure, and specialized support options that fit your specific requirements.`
    }
  ];

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems?.has(index)) {
      newOpenItems?.delete(index);
    } else {
      newOpenItems?.add(index);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-semibold text-foreground mb-2">Frequently Asked Questions</h3>
        <p className="text-muted-foreground">
          Get answers to common questions about our pricing and billing
        </p>
      </div>
      <div className="space-y-4">
        {faqData?.map((item, index) => (
          <div key={index} className="border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleItem(index)}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted/30 transition-colors duration-200"
            >
              <span className="font-medium text-foreground pr-4">{item?.question}</span>
              <Icon 
                name={openItems?.has(index) ? "ChevronUp" : "ChevronDown"} 
                size={20} 
                className="text-muted-foreground flex-shrink-0 transition-transform duration-200"
              />
            </button>
            
            {openItems?.has(index) && (
              <div className="px-6 pb-4 animate-slide-down">
                <div className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {item?.answer}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-8 p-6 bg-primary/5 border border-primary/20 rounded-lg text-center">
        <div className="flex items-center justify-center space-x-2 mb-3">
          <Icon name="MessageCircle" size={20} className="text-primary" />
          <h4 className="text-lg font-semibold text-foreground">Still have questions?</h4>
        </div>
        <p className="text-muted-foreground mb-4">
          Our sales team is here to help you find the perfect plan for your needs
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200">
            <Icon name="MessageSquare" size={16} className="mr-2" />
            Contact Sales
          </button>
          <button className="inline-flex items-center justify-center px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted/50 transition-colors duration-200">
            <Icon name="Calendar" size={16} className="mr-2" />
            Schedule Demo
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingFAQ;